import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithProvider } from "@/redux/baseQuery";
import { createSearchParams } from "@/utils";
import { transformClient, transformCompany, transformDriver, transformVehicle } from "./helpers";
import {
  SearchClientParams,
  SearchClientResponse,
  SearchCompanyParams,
  SearchCompanyResponse,
  SearchDriverParams,
  SearchDriverResponse,
  SearchVehicleParams,
  SearchVehicleResponse,
} from "./types";

/**
 * Rows returned by a client typeahead search. The API's own default is 4, which silently
 * truncated every caller that omitted the param — searching an operator with dozens of matching
 * clients surfaced only 4 of them, with no signal that the list was cut. 12 is what the mobile
 * search modal already passed explicitly; this makes the desktop header search, booking
 * reassignment and the company associated-clients form agree with it.
 */
export const CLIENT_SEARCH_PAGE_SIZE = 12;

export const typeaheadApi = createApi({
  reducerPath: "typeaheadApi",
  baseQuery: baseQueryWithProvider("typeahead"),
  endpoints: (builder) => ({
    searchClient: builder.query<SearchClientResponse, SearchClientParams>({
      query: ({ q, page_size = CLIENT_SEARCH_PAGE_SIZE }) => `/client?${createSearchParams({ q, page_size }).toString()}`,
      transformResponse: transformClient,
    }),
    searchCompany: builder.query<SearchCompanyResponse, SearchCompanyParams>({
      query: ({ q }) => `/company?${createSearchParams({ q }).toString()}`,
      transformResponse: transformCompany,
    }),
    searchDriver: builder.query<SearchDriverResponse, SearchDriverParams>({
      query: ({ q, job_uuid }) => `/driver?${createSearchParams({ q, job_uuid }).toString()}`,
      transformResponse: transformDriver,
    }),
    searchVehicle: builder.query<SearchVehicleResponse, SearchVehicleParams>({
      query: ({ q, job_uuid, page_size = 99 }) => `/vehicle?${createSearchParams({ q, job_uuid, page_size }).toString()}`,
      transformResponse: transformVehicle,
    }),
  }),
});

export const { useLazySearchClientQuery, useLazySearchCompanyQuery, useLazySearchDriverQuery, useLazySearchVehicleQuery } = typeaheadApi;
